import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import theme from '../../constants/Theme';
import { DiaryEntry, DIARY_ENTRY_TYPE } from '../../types/diaryTypes';
import {
  formatDateWithWeekday,
  getYear,
  isDateInCurrentYear,
  isDateThisWeek,
} from '../../helpers/dateHelpers';
import Rating from '../Rating/Rating';
import SoundRating from '../SoundRating/SoundRating';
import TryOutDevices from '../TryOutDevices/TryOutDevices';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Textlink from '../../elements/Textlink';
import ChevronDown from '../../assets/vectors/ChevronDown';

export interface DiaryTableRef {
  scrollToTop: () => void;
}

interface DiaryTableProps {
  entries: DiaryEntry[];
  isLoading: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  hasMore: boolean | undefined;
}

const DiaryTable = forwardRef<DiaryTableRef, DiaryTableProps>(
  ({ entries, isLoading, onLoadMore, isLoadingMore, hasMore }, ref) => {
    const classes = useStyles();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      },
    }));

    const renderEntryRows = (entries: DiaryEntry[]) => {
      if (isLoading) return <></>;
      if (!entries || entries.length === 0) {
          return (
            <tr>
              <td colSpan={7} className={classes.emptyRow}>
              Es sind keine Einträge vorhanden.
            </td>
          </tr>
        );
      }

      return entries.map((entry, index, arr) => {
        const prevEntry = index > 0 ? arr[index - 1] : null;
        const prevDateFormatted = prevEntry ? formatDateWithWeekday(prevEntry.date) : null;
        const currentDateFormatted = formatDateWithWeekday(entry.date);
        const hasSameDateAsPrevious = prevEntry && prevDateFormatted === currentDateFormatted;
        const firstRow = index === 0;
        const hasSoundData = [entry.sound, entry.speechComprehension, entry.volume].every(
          attributes => Array.isArray(attributes) && attributes.length > 0
        );
        const entryType = (entry as { type?: string }).type;
        const entryTypeLabel = entryType === DIARY_ENTRY_TYPE.HEARING_JOURNEY_TASK ? 'Hörerlebnis' : 'Tagebuch';

        return (
          <tr key={entry.id} className={!firstRow ? classes.rowSeperator : ''}>
            <td className={`${classes.tableCell} ${hasSameDateAsPrevious ? classes.sameDateCell : ''}`}>
              {!hasSameDateAsPrevious && currentDateFormatted}
            </td>
              <td className={classes.tableCell}>{entryTypeLabel}</td>
            <td className={classes.tableCell}>{entry.situation}</td>
            <td className={classes.tableCell}>
              <Rating index={entry.experience} />
            </td>
            <td className={classes.tableCell}>
              {hasSoundData ? (
                <SoundRating
                  sound={entry.sound}
                  speechComprehension={entry.speechComprehension}
                  volume={entry.volume}
                />
              ) : null}
            </td>
            <td className={classes.tableCell}>
              <TryOutDevices tryOutDeviceList={entry.fittingActivity?.tryOutDeviceList || []} size="small" />
            </td>
            <td className={classes.tableCell}>{entry.notes}</td>
          </tr>
        );
      });
    };

    const renderSection = (title: string, entries: DiaryEntry[]) => {
      if (!entries || entries.length === 0) return null;

      return (
        <>
          <tr className={classes.sectionHeader}>
              <td colSpan={7}>
              <div>{title}</div>
            </td>
          </tr>
          {renderEntryRows(entries)}
        </>
      );
    };

    const currentWeekEntries = entries.filter(entry => isDateThisWeek(entry.date));
    const currentYearOlderEntries = entries.filter(
      entry => !isDateThisWeek(entry.date) && isDateInCurrentYear(entry.date)
    );

    const previousYearsEntries = entries.filter(entry => !isDateInCurrentYear(entry.date));
    const yearGroups = previousYearsEntries.reduce<Record<number, DiaryEntry[]>>((acc, entry) => {
      const year = getYear(entry.date);
      if (year === null) {
        return acc;
      }
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(entry);
      return acc;
    }, {});

    const sortedYears = Object.keys(yearGroups)
      .map(Number)
      .sort((a, b) => b - a);

    return (
      <div className={classes.container}>
        <LoadingOverlay isLoading={isLoading} />
        <div className={classes.tableWrapper}>
          <div className={classes.tableScrollContainer} ref={scrollContainerRef}>
            <table className={classes.table}>
              <colgroup>
                <col className={classes.dateCol} />
                <col className={classes.typeCol} />
                <col className={classes.situationCol} />
                <col className={classes.RatingCol} />
                <col className={classes.soundRatingCol} />
                <col className={classes.hearingAidCol} />
                <col className={classes.notesCol} />
              </colgroup>
              <thead className={classes.tableHeader}>
                <tr>
                  <th>Datum</th>
                  <th>Typ</th>
                  <th>Situation</th>
                  <th>Bewert.</th>
                  <th>Klangbewertung</th>
                  <th>Hörgerät</th>
                  <th>Notizen</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  renderEntryRows([])
                ) : (
                  <>
                    {renderSection('Diese Woche', currentWeekEntries)}
                    {renderSection('Ältere Einträge', currentYearOlderEntries)}
                    {sortedYears.map(year => renderSection(`${year}`, yearGroups[year]))}
                  </>
                )}
                {hasMore === true && !isLoading && (
                  <tr>
                      <td colSpan={7}>
                      <div className={classes.loadMoreContainer}>
                        <Textlink
                          title="Mehr Einträge laden"
                          onClick={onLoadMore}
                          fontSize={14}
                          iconRight={ChevronDown}
                          loading={isLoadingMore}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
);

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.contrastWhite,
    padding: 10,
    borderRadius: theme.borderRadius.card,
    boxShadow: theme.shadows.card,
    height: '100%',
    position: 'relative',
    minWidth: '710px',
    minHeight: '500px',
    marginBottom: 30,
  },
  tableWrapper: {
    position: 'absolute',
    top: 10,
    left: 10,
    right: 10,
    bottom: 10,
  },
  tableScrollContainer: {
    height: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: 5,
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      marginTop: 45,
      background: theme.colors.grey50,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.grey300,
      borderRadius: '4px',
      '&:hover': {
        background: theme.colors.greyMain,
      },
    },
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    tableLayout: 'auto',
    width: '100%',
  },
  tableHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.colors.contrastWhite,
    zIndex: 1,
    '& th': {
      fontWeight: 700,
      fontSize: 14,
      padding: '10px 5px 10px 5px',
      color: theme.colors.contrastBlack,
      textAlign: 'left',
      borderBottom: `2px solid ${theme.colors.blueLight}`,
    },
  },
  tableFooter: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    right: 10,
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
  },
  dateCol: {
    minWidth: 88,
    width: 90,
  },
  typeCol: {
    minWidth: 95,
    width: 120,
  },
  situationCol: {
    minWidth: 95,
    width: 150,
  },
  RatingCol: {
    minWidth: 88,
    width: 200,
  },
  soundRatingCol: {
    minWidth: 110,
    width: 220,
  },
  hearingAidCol: {
    minWidth: 120,
    width: 220,
  },
  notesCol: {
    minWidth: 88,
    width: 'auto',
  },
  sectionHeader: {
    '& td': {
      padding: 0,
    },
    '& div': {
      fontSize: 14,
      backgroundColor: theme.colors.blueLight,
      marginTop: 10,
      marginBottom: 10,
      color: theme.colors.blue,
      fontWeight: 700,
      padding: '0.75rem 1rem',
      borderRadius: 66,
    },
  },
  emptyRow: {
    textAlign: 'left',
    padding: 10,
  },
  rowSeperator: {
    '& td': {
      borderTop: `1px solid ${theme.colors.blueLight}`,
    },
  },
  tableCell: {
    maxHeight: 150,
    padding: '10px 5px 10px 5px',
    verticalAlign: 'top',
  },
  sameDateCell: {
    borderTop: `1px solid ${theme.colors.contrastWhite} !important`,
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0 10px 0',
  },
});

export default DiaryTable;
